<script>
import deepLink from '@/mixins/deeplink.js'
import { mapGetters } from 'vuex'

import axios from 'axios'
import ENV from '@src/env'
import Raven from 'raven-js'

import GTM_CONSTANTS from '@constants/google-tag-manager'
import AMPLITUDE from '@constants/amplitude'

export default {
  name: 'RatingWidget',
  mixins: [deepLink()],
  props: {
    componentData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      thumbsUpSelected: false,
      thumbsDownSelected: false,
    }
  },
  computed: {
    ...mapGetters('widgets', ['userEnv']),
    token() {
      return this.$route.query.token || {}
    },
    getThumbsUpImage() {
      return 'https://img.rappi.com/support/ratings/thumbsUp.png'
    },
    getThumbsDownImage() {
      return 'https://img.rappi.com/support/ratings/thumbsDown.png'
    },
    getThumbsUpCode() {
      const { ratings } = this.componentData.widgetData
      return ratings.question.length > 0 && ratings.question[0].options[0].code
    },
    getThumbsDownCode() {
      const { ratings } = this.componentData.widgetData
      return ratings.question.length > 0 && ratings.question[0].options[1].code
    },
    getRatingId() {
      const { ratings } = this.componentData.widgetData
      return ratings.question.length > 0 && ratings.question[0].rating_id
    },
    getOrderId() {
      const { widgetData } = this.componentData
      return widgetData.ratings && widgetData.ratings.order_id
    },
    getQuestionId() {
      const { ratings } = this.componentData.widgetData
      return ratings.question.length > 0 && ratings.question[0].id
    },
    getDeeplinkThumbsDown() {
      const { ratings } = this.componentData.widgetData
      const thumbDownOption = ratings.question[0].options.filter(
        ({ code }) => code === 'rating_widget_options_thumbs_down'
      )
      return thumbDownOption[0].params.action.url
    },
    getDeeplinkThumbsUp() {
      const { ratings } = this.componentData.widgetData
      const thumbDownOption = ratings.question[0].options.filter(
        ({ code }) => code === 'rating_widget_options_thumbs_up'
      )
      return thumbDownOption[0].params.action.url
    },
    getDeeplinkParams(deep) {
      return deep.split('?')[1] ? deep.split['?'][1] : ''
    },
    getDeeplinkThumbDowType() {
      return this.componentData.deeplinkThumbsDownType
    },
    getDeeplinkThumbUpType() {
      return this.componentData.deeplinkThumbsUpType
    }
  },
  methods: {
    async thumbsUpRating() {
      try {
        this.thumbsUpSelected = true
        const ratingThumbsUp = this.generateRating()
        await this.callRatingsEndpoint(ratingThumbsUp)
      } catch (error) {
        Raven.captureException(error)
      } finally {
        const typeOfDeeplink = this.getDeeplinkThumbUpType
        if (typeOfDeeplink === 'deeplink') {
          this.callDeepLink('goto=home')
        } else {
          this.thirdPartyDeepLink(this.getDeeplinkThumbsUp)
        }
      }
    },
    async thumbsDownRating() {
      try {
        this.thumbsUpSelected = false
        const ratingThumbsDown = this.generateRating()
        await this.callRatingsEndpoint(ratingThumbsDown)
      } catch (error) {
        Raven.captureException(error)
      } finally {
        const typeOfDeeplink = this.getDeeplinkThumbDowType
        if (typeOfDeeplink === 'deeplink') {
          this.callDeepLink('goto=home')
        } else {
          this.thirdPartyDeepLink(this.getDeeplinkThumbsDown)
        }
      }
    },
    generateAnswer(code = 'rating_widget_options') {
      return [
        {
          question_id: this.getQuestionId,
          code: code,
        },
      ]
    },
    generateRating() {
      this.logEvent(AMPLITUDE.RATING_CREATED)

      const code = this.thumbsUpSelected
        ? this.getThumbsUpCode
        : this.getThumbsDownCode
      return {
        rating_id: this.getRatingId,
        order_id: this.getOrderId,
        answer: this.generateAnswer(code),
      }
    },
    callRatingsEndpoint(rating) {
      const baseRatings = `${
        ENV.rappi.url[this.userEnv]
      }/api/support-ratings/send`

      return axios.post(baseRatings, rating, {
        headers: { Authorization: `Bearer ${this.token}` },
      })
    },
    logEvent(eventType) {
      const { screenId } = this.$route.params
      const {
        country_code: countryCode,
        dev,
        order_id: orderID,
      } = this.$route.query
      const payload = { screenId, countryCode, dev, orderID }
      this.$gtm.trackEvent({
        payload,
        eventType,
        action: eventType,
        category: 'RATING_ON_SWAPP',
        event: GTM_CONSTANTS.LOG_EVENT,
        noninteraction: false,
      })
    },
  },
}
</script>

<template>
  <section :class="$style.container">
    <aside :class="$style.container__ratings">
      <button
        :class="$style.ratings__thumbs"
        @click="thumbsDownRating">
        <img
          :src="getThumbsDownImage"
          width="50"
          height="50" >
      </button>
      <button
        :class="$style.ratings__thumbs"
        @click="thumbsUpRating">
        <img
          :src="getThumbsUpImage"
          width="50"
          height="50" >
      </button>
    </aside>
  </section>
</template>

<style scoped lang="scss" module>
@import '@design';

.container {
  display: flex;
  justify-content: center;
  padding: 1rem;
  margin: 0 auto;

  &__ratings {
    display: flex;
    flex-flow: row wrap;
    justify-content: space-between;
    width: 150px;

    &__thumbs {
      cursor: pointer;
      box-shadow: $regular-box-shadow;
    }
  }
}
</style>
